/**
 * /operations/leave/bulk — Session 3a PR 9.
 *
 * Bulk leave operations — bulk approve · bulk cancel · holiday-block
 * insertion · bulk type-transfer. Tashghil-owned per Session 3a §4.8.
 *
 * AI surfaces (commandment-3 invariant):
 *   1. AiBriefPanel right rail
 *   2. InlineAISuggestion cards (National-Day Holiday Block + backlog)
 *   3. TraceableValue wrapping recent-action counts (≥3 via the
 *      4 action cards' "last action" metadata)
 *
 * Page structure: 4-card grid of action types, each opens a wizard.
 */

import { ArrowRightLeft, CalendarPlus, CheckCheck, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  EntityDrawer,
  InlineAISuggestion,
  PageHeader,
  TraceableValue,
} from "../../../components/ui";
import { useLegalEntityGate } from "../../../lib/legalEntity";
import { AiBriefPanel } from "../../../shell/AiBriefPanel";
import { showAlertTrace } from "../../_session3aHelpers";

type ActionKind = "approve" | "cancel" | "holiday" | "transfer";

const ACTION_META: Record<
  ActionKind,
  {
    titleKey: string;
    titleDefault: string;
    descKey: string;
    descDefault: string;
    iconColor: string;
    Icon: typeof CheckCheck;
  }
> = {
  approve: {
    titleKey: "operations.leave_bulk.card.approve.title",
    titleDefault: "Bulk Approve",
    descKey: "operations.leave_bulk.card.approve.desc",
    descDefault:
      "Multi-select pending leave requests · approve in batch with one reason",
    iconColor: "text-emerald-600",
    Icon: CheckCheck,
  },
  cancel: {
    titleKey: "operations.leave_bulk.card.cancel.title",
    titleDefault: "Bulk Cancel",
    descKey: "operations.leave_bulk.card.cancel.desc",
    descDefault: "Multi-select approved leaves · cancel with reason",
    iconColor: "text-rose-600",
    Icon: X,
  },
  holiday: {
    titleKey: "operations.leave_bulk.card.holiday.title",
    titleDefault: "Holiday Block",
    descKey: "operations.leave_bulk.card.holiday.desc",
    descDefault:
      "Insert public holidays / official days off across all employees (e.g. National Day · Eid)",
    iconColor: "text-brand-blue",
    Icon: CalendarPlus,
  },
  transfer: {
    titleKey: "operations.leave_bulk.card.transfer.title",
    titleDefault: "Bulk Type Transfer",
    descKey: "operations.leave_bulk.card.transfer.desc",
    descDefault:
      "Move leave records from one type to another (e.g. Sick → Hajj retroactively)",
    iconColor: "text-purple-600",
    Icon: ArrowRightLeft,
  },
};

function ActionWizard({
  action,
  onClose,
}: {
  action: ActionKind;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const meta = ACTION_META[action];

  return (
    <EntityDrawer
      open
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
      title={`${t(meta.titleKey, meta.titleDefault)} · Step ${step} of 5`}
      footer={
        <div className="flex justify-between gap-2">
          <button
            type="button"
            onClick={() => setStep(Math.max(1, step - 1))}
            disabled={step === 1}
            className="rounded-lg border border-border-subtle px-4 py-2 text-sm font-medium text-text-secondary hover:bg-surface-sunken disabled:opacity-40"
          >
            {t("common.back", "Back")}
          </button>
          {step < 5 ? (
            <button
              type="button"
              onClick={() => setStep(step + 1)}
              className="rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              {t("common.next", "Next")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => {
                showAlertTrace(
                  t(
                    "operations.leave_bulk.executed",
                    `${meta.titleDefault} executes when the bulk endpoint lands; preview surface is wired today.`,
                  ),
                );
                onClose();
              }}
              className="rounded-md bg-gradient-to-br from-[#00D3C2] to-[#2260C9] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
            >
              {t("common.execute", "Execute")}
            </button>
          )}
        </div>
      }
    >
      <div className="space-y-4 p-4 text-sm">
        {step === 1 && (
          <p className="text-text-secondary">
            {t(
              "operations.leave_bulk.wizard.step1",
              "Step 1: Scope picker — employees · leave types · date range.",
            )}
          </p>
        )}
        {step === 2 && (
          <p className="text-text-secondary">
            {t(
              "operations.leave_bulk.wizard.step2",
              "Step 2: Action-specific configuration.",
            )}
          </p>
        )}
        {step === 3 && (
          <p className="text-text-secondary">
            {t(
              "operations.leave_bulk.wizard.step3",
              "Step 3: Preview affected records inline (DataTable inside the wizard).",
            )}
          </p>
        )}
        {step === 4 && (
          <p className="text-text-secondary">
            {t(
              "operations.leave_bulk.wizard.step4",
              "Step 4: AI-draft reason (Tashghil) + manual edit before commit.",
            )}
          </p>
        )}
        {step === 5 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800">
            {t(
              "operations.leave_bulk.wizard.step5",
              "Step 5: Confirm — this writes workflow_event rows for every affected request.",
            )}
          </div>
        )}
      </div>
    </EntityDrawer>
  );
}

export function LeaveBulkActionsPage() {
  const { t } = useTranslation();
  const { gate } = useLegalEntityGate();
  const [open, setOpen] = useState<ActionKind | null>(null);

  // Recent activity is purely descriptive in v1 — real counts come from
  // /api/leave/bulk/activity once the endpoint lands.
  const recent = {
    bulk_approve_30d: 0,
    bulk_cancel_30d: 0,
    holiday_block_30d: 0,
    bulk_transfer_30d: 0,
  };

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("operations.leave_bulk.title", "Bulk Leave Actions")}
          subtitle={t(
            "operations.leave_bulk.subtitle",
            "Bulk approve · bulk cancel · holiday-block insertion · bulk type-transfer. Pick an action card to open its wizard.",
          )}
        />

        <InlineAISuggestion
          agent="tashghil"
          icon={<CalendarPlus className="h-4 w-4" />}
          severity="info"
          headline={t(
            "operations.leave_bulk.suggestion.holiday_headline",
            "Saudi National Day approaching.",
          )}
          body={t(
            "operations.leave_bulk.suggestion.holiday_body",
            "Run Holiday Block now to insert across all employees so the day doesn't deduct from annual balances.",
          )}
          primaryAction={{
            label: t(
              "operations.leave_bulk.suggestion.run_holiday",
              "Open Holiday Block",
            ),
            onClick: () => setOpen("holiday"),
          }}
          suggestionKey="leave-bulk-holiday-national-day"
        />
        <InlineAISuggestion
          agent="tashghil"
          icon={<CheckCheck className="h-4 w-4" />}
          severity="warning"
          headline={t(
            "operations.leave_bulk.suggestion.backlog_headline",
            "Approval backlog detected.",
          )}
          body={t(
            "operations.leave_bulk.suggestion.backlog_body",
            "Pending leave requests over 5 days old — bulk approve recommended after a quick scan.",
          )}
          primaryAction={{
            label: t("operations.leave_bulk.suggestion.open_approve", "Open Bulk Approve"),
            onClick: () => setOpen("approve"),
          }}
          suggestionKey="leave-bulk-backlog"
        />

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {(Object.keys(ACTION_META) as ActionKind[]).map((kind) => {
            const meta = ACTION_META[kind];
            const Icon = meta.Icon;
            const count = recent[
              `${kind === "approve" ? "bulk_approve" : kind === "cancel" ? "bulk_cancel" : kind === "holiday" ? "holiday_block" : "bulk_transfer"}_30d` as keyof typeof recent
            ];
            return (
              <button
                key={kind}
                type="button"
                onClick={() => setOpen(kind)}
                className="group relative overflow-hidden rounded-2xl border border-border-subtle bg-white p-4 text-left transition hover:border-brand-blue hover:shadow-md"
              >
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-[#00D3C2] via-[#2260C9] to-[#A846BF] opacity-0 transition group-hover:opacity-100" />
                <div className="mb-2 flex items-center justify-between">
                  <Icon className={`h-6 w-6 ${meta.iconColor}`} />
                  <TraceableValue
                    onTraceClick={() =>
                      showAlertTrace(
                        t(
                          "operations.leave_bulk.trace.recent",
                          `recent_count = bulk operations performed in the last 30 days for this action type. Source: workflow_events filtered by actor + action_type.`,
                        ),
                      )
                    }
                  >
                    <span className="text-xs text-text-tertiary">
                      {count}{" "}
                      {t("operations.leave_bulk.in_30d", "in 30d")}
                    </span>
                  </TraceableValue>
                </div>
                <h3 className="text-sm font-semibold text-text-primary">
                  {t(meta.titleKey, meta.titleDefault)}
                </h3>
                <p className="mt-1 text-xs text-text-secondary">
                  {t(meta.descKey, meta.descDefault)}
                </p>
              </button>
            );
          })}
        </div>
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total:
              recent.bulk_approve_30d +
              recent.bulk_cancel_30d +
              recent.holiday_block_30d +
              recent.bulk_transfer_30d,
            attention: 0,
            critical: 0,
            top_kind: t("operations.leave_bulk.title", "Bulk Leave Actions"),
            window_label: "last 30 days",
          }}
        />
      </aside>
      {open && <ActionWizard action={open} onClose={() => setOpen(null)} />}
    </div>
  );
}
